import React, { useMemo } from 'react';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from 'recharts';

import { useGetExerciseProperties } from '../../../redux/api/exerciseConfigAPI';
import { IExercise } from '../../../types/interfaces/exercise';
import { ITraining } from '../../../types/interfaces/training';
import {
  getPolarRadiusTicks,
  getSummarizedSkillsRadarChartDataFromTrainings,
} from '../../../utils/charts/charts-utils';
import { renderSkillsPolarAngleAxisTick } from '../recharts-polar-angle-tick/renderPolarAngleAxisTick';
import { useStyles } from './AppTrainingsSkillsRadarChart.styles';

export interface IAppTrainingsSkillsRadarChartProps {
  trainings: ITraining[] | null | undefined;
  exercises: IExercise[] | undefined;
  height?: number;
  yAxisSegments?: number;
}

export default function AppTrainingsSkillsRadarChart({
  trainings,
  exercises,
  height = 300,
  yAxisSegments = 5,
}: IAppTrainingsSkillsRadarChartProps) {
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { data: exerciseProperties } = useGetExerciseProperties();

  const radarChartData = useMemo(() => {
    if (!trainings || !exerciseProperties || !exercises) {
      return [];
    }

    return getSummarizedSkillsRadarChartDataFromTrainings(
      trainings,
      exercises,
      exerciseProperties,
    );
  }, [trainings, exercises, exerciseProperties]);

  if (radarChartData.length === 0) {
    return null;
  }

  const yAxisMax = radarChartData[0]?.fullMark || 100;
  const polarRadiusTicks = getPolarRadiusTicks(yAxisMax, yAxisSegments);

  return (
    <Box className={classes.wrapper}>
      <ResponsiveContainer width="100%" height={height}>
        <RadarChart data={radarChartData}>
          <PolarGrid />
          {React.createElement(PolarAngleAxis as any, {
            dataKey: 'skill',
            tick: renderSkillsPolarAngleAxisTick,
          })}
          <PolarRadiusAxis
            angle={90}
            type="number"
            domain={[0, yAxisMax]}
            ticks={polarRadiusTicks as any}
            tick={false}
            axisLine={false}
          />
          <Radar
            name="Value"
            dataKey="value"
            stroke={theme.palette.primary.main}
            fill={theme.palette.primary.main}
            fillOpacity={0.6}
          />
        </RadarChart>
      </ResponsiveContainer>
    </Box>
  );
}
